import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORY_LABEL, RISK_CLASS, RISK_LABEL, fmtTimeAgo } from "@/lib/labels";
import JourneyBreakPanel from "@/components/journeys/JourneyBreakPanel";
import { ChevronLeft, ChevronDown, Send, Check, AlertTriangle, Sparkles, Shield, Clock, FileText, UserCircle2, Loader2 } from "lucide-react";

const STATUSES = ["new", "triaged", "in_progress", "waiting_internal", "waiting_customer", "resolved", "closed"];

export default function CaseWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [signal, setSignal] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [proposingId, setProposingId] = useState(null);
  const [executingId, setExecutingId] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showRecHistory, setShowRecHistory] = useState(false);
  const [showAllDone, setShowAllDone] = useState(false);
  const [toast, setToast] = useState("");
  const [links, setLinks] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/cases/${id}`);
    setC(data);
    // A case with no external ticket is the common one, so this failing or
    // returning nothing must not affect the rest of the screen.
    api.get(`/cases/${id}/links`)
      .then((r) => setLinks(r.data.links || []))
      .catch(() => setLinks([]));
    setReply(data?.intelligence?.suggested_reply || "");
    if (data.signal_ids?.[0]) {
      const { data: sigs } = await api.get(`/signals`);
      setSignal(sigs.find((s) => s.id === data.signal_ids[0]) || null);
    }
    if (data.customer_id) {
      const { data: c360 } = await api.get(`/customers/${data.customer_id}`);
      setCustomer(c360);
    }
  };

  useEffect(() => { load(); }, [id]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3600); };

  const resync = async (link) => {
    setBusy(true);
    try {
      await api.post(`/cases/${id}/links/${link.id}/resync`);
      const { data } = await api.get(`/cases/${id}/links`);
      setLinks(data.links || []);
      flash(`Syncing to ${link.external_id} again.`);
    } catch (e) {
      flash(e?.response?.data?.message || "Could not resume syncing");
    } finally {
      setBusy(false);
    }
  };

  const runIntel = async () => {
    setBusy(true);
    setRerunning(true);
    try {
      const { data } = await api.post(`/cases/${id}/assess`);
      if (!data.available) {
        // 05: an AI outage must not read as a broken case.
        flash(data.reason || "AI enrichment is unavailable — the case is unaffected.");
        return;
      }
      await load();
      flash("Assessment updated");
    } catch (e) {
      flash(e?.response?.data?.message || "Assessment failed");
    } finally { setBusy(false); setRerunning(false); }
  };

  const draftReply = async () => {
    setBusy(true);
    setDrafting(true);
    try {
      const { data } = await api.post(`/cases/${id}/draft-reply`, { guidance: "" });
      if (!data.available) {
        flash(data.reason || "AI drafting is unavailable.");
        return;
      }
      setReply(data.draft.body);
      flash("Draft ready — review before sending.");
    } catch (e) {
      flash(e?.response?.data?.message || "Drafting failed");
    } finally { setBusy(false); setDrafting(false); }
  };
  const setStatus = async (s) => {
    setBusy(true);
    try {
      await api.post(`/cases/${id}/status`, { status: s });
      await load();
      flash(`Status → ${s}`);
    } catch (e) {
      // The case state machine is enforced server-side; show why it refused.
      flash(e?.response?.data?.message || "Status change rejected");
    } finally { setBusy(false); }
  };
  // An internal note is a low-risk action: the policy engine allows it
  // outright, but it still goes through the same pipeline and audit trail.
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${id}/actions`, {
        action_type: "add_internal_note",
        payload: { text: note },
      });
      await api.post(`/actions/${data.id}/execute`);
      setNote("");
      await load();
      flash("Note added.");
    } catch (e) {
      flash(e?.response?.data?.message || "Could not add the note");
    } finally { setBusy(false); }
  };
  // A customer reply is an action like any other: proposed, policy-evaluated,
  // approved by a human, then executed. The UI cannot bypass that, and the
  // approve/execute controls appear in the Actions panel.
  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${id}/actions`, {
        action_type: "send_customer_reply",
        payload: { body: reply },
        evidence_refs: (c.signals || []).map((sig) => sig.id),
        confidence: 1.0,
      });
      await load();
      flash(data.policy?.explanation || "Reply proposed — approval required before sending.");
    } catch (e) {
      flash(e?.response?.data?.message || "Could not propose the reply");
    } finally { setBusy(false); }
  };
  const recommend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${id}/recommend`);
      if (!data.available) { flash("AI recommendations are unavailable."); return; }
      await load();
      const n = data.recommendations.length;
      flash(`${n} recommendation${n === 1 ? "" : "s"}`
        + (data.revision > 1 ? ` · v${data.revision}; earlier ones moved to history` : ""));
    } catch (e) {
      flash(e?.response?.data?.message || "Could not generate recommendations");
    } finally { setBusy(false); }
  };

  const proposeAction = async (rec) => {
    setBusy(true);
    setProposingId(rec.id);
    try {
      const { data } = await api.post(`/cases/${id}/actions`, {
        action_type: rec.kind,
        payload: rec.payload || {},
        evidence_refs: rec.evidence_refs || [],
        confidence: rec.confidence ?? 1.0,
        recommendation_id: rec.id,
      });
      await load();
      // The policy engine, not the UI, decided what happens next.
      flash(data.policy?.explanation || `Action ${data.status}`);
    } catch (e) {
      flash(e?.response?.data?.message || "Action was not permitted");
    } finally { setBusy(false); setProposingId(null); }
  };

  const approveAction = async (actionId) => {
    setBusy(true);
    try {
      await api.post(`/actions/${actionId}/approve`);
      await load();
      flash("Approved — it can now be executed.");
    } catch (e) {
      flash(e?.response?.data?.message || "Approval failed");
    } finally { setBusy(false); }
  };

  const rejectAction = async (actionId) => {
    const reason = window.prompt("Why are you rejecting this action?") || "";
    setBusy(true);
    try {
      await api.post(`/actions/${actionId}/reject`, { reason });
      await load();
      flash("Rejected.");
    } catch (e) {
      flash(e?.response?.data?.message || "Rejection failed");
    } finally { setBusy(false); }
  };

  const executeAction = async (actionId) => {
    setBusy(true);
    setExecutingId(actionId);
    try {
      const { data } = await api.post(`/actions/${actionId}/execute`);
      await load();
      flash(data.result?.detail || "Executed.");
    } catch (e) {
      flash(e?.response?.data?.message || "Execution failed");
    } finally { setBusy(false); setExecutingId(null); }
  };

  if (!c) return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading case…</div>;

  const intel = c.intelligence;
  // Actions still waiting on someone, and the finished ones newest first.
  const openActions = (c.actions || []).filter((a) => !DONE_ACTION.has(a.status));
  const doneActions = (c.actions || []).filter((a) => DONE_ACTION.has(a.status))
    .sort((x, y) => new Date(y.executed_at || y.created_at || 0) - new Date(x.executed_at || x.created_at || 0));
  const dec = c.decision;
  // A customer-facing reply always requires human approval in the MVP — the
  // policy engine enforces it regardless of what this page shows, so there is
  // no "quick send" path to offer.
  const replyRequiresApproval = true;
  const cust = customer?.customer;

  return (
    <div className="flex flex-col lg:h-full">
      {/* sub-header */}
      <div className="px-4 md:px-6 py-3 border-b flex items-center justify-between flex-wrap gap-3" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 min-w-0">
          <button onClick={() => nav(-1)} className="p-1 rounded hover-wash" data-testid="back-btn"><ChevronLeft size={16} /></button>
          <span className="font-mono text-[11.5px]" style={{ color: "var(--text-muted)" }}>{c.case_ref}</span>
          <span className={`badge ${RISK_CLASS[c.risk_type]}`}>{RISK_LABEL[c.risk_type]}</span>
          <span className="badge b-muted">{CATEGORY_LABEL[c.category] || c.category || "general"}</span>
          <span className="badge b-muted">{c.status}</span>
          <div className="font-display text-[15px] truncate max-w-full md:max-w-[460px]" data-testid="case-title">{c.title}</div>
        </div>
        <div className="flex items-center flex-wrap gap-1.5">
          <button className="btn-ghost btn" data-testid="run-intel" onClick={runIntel} disabled={busy}>
            <Sparkles size={13} className={rerunning ? "animate-spin" : ""} /> {rerunning ? "Running…" : "Re-run AI"}
          </button>
          <button className="btn-ghost btn" data-testid="recommend" onClick={recommend} disabled={busy}>
            <Shield size={13} /> Recommend actions
          </button>
          <select data-testid="status-select" className="input" style={{ width: "auto", padding: "6px 10px" }}
                  value={c.status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>

      {toast && <div className="fixed top-[72px] right-4 z-50 badge b-healthy shadow-lg" data-testid="toast">{toast}</div>}

      {/* Stacked below lg, where the conversation and AI panel come before
          the timeline so the case itself is on screen first. */}
      <div className="grid grid-cols-12 flex-1 lg:min-h-0">
        {/* LEFT: Timeline */}
        <div className="col-span-12 lg:col-span-3 order-3 lg:order-none lg:border-r lg:overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="case-timeline">
          <div className="px-5 py-4 sticky top-0" style={{ background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
            <div className="text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>Timeline</div>
          </div>
          <div className="px-5 py-3 space-y-3">
            {(c.timeline || []).slice().reverse().map((e, i) => (
              <div key={i} className="text-[12px]" data-testid={`timeline-${e.kind}-${i}`}>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: e.kind === "decision_made" ? "var(--risk-product)" : e.kind === "intelligence_generated" ? "var(--risk-waiting)" : e.kind === "reply_sent" ? "var(--risk-healthy)" : "var(--text-secondary)" }} />
                  <span className="font-mono text-[10.5px]" style={{ color: "var(--text-muted)" }}>{fmtTimeAgo(e.at)}</span>
                </div>
                <div className="pl-3 mt-0.5">
                  <div className="leading-snug">{e.title}</div>
                  {e.detail && <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{e.detail}</div>}
                  <div className="text-[10.5px] mt-0.5" style={{ color: "var(--text-muted)" }}>by {e.actor}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MIDDLE: Conversation */}
        <div className="col-span-12 lg:col-span-5 order-1 lg:order-none border-b lg:border-b-0 lg:border-r lg:overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="case-conversation">
          <div className="px-4 md:px-6 py-5">
            {c.journey ? (
              // A broken journey belongs to everyone stuck in it, not one
              // customer, so there is nobody to reply to: the funnel is the evidence.
              <JourneyBreakPanel journey={c.journey} />
            ) : (
              <div className="flex items-center gap-2 mb-1.5">
                <UserCircle2 size={16} style={{ color: "var(--text-secondary)" }} />
                <span className="font-medium text-[13px]">{c.customer_name}</span>
                <span className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>{c.customer_email}</span>
              </div>
            )}
            {signal && (
              <div className="card p-5">
                <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                  {signal.source_type} · {fmtTimeAgo(signal.created_at_source)}
                </div>
                <div className="font-medium mb-2">{signal.subject}</div>
                <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text)" }}>{signal.message}</div>
              </div>
            )}

            {!c.journey && (<>
            {/* Reply composer */}
            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Send size={13} style={{ color: "var(--text-secondary)" }} />
                <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Reply to customer</div>
                {replyRequiresApproval && (
                  <span className="badge b-rev">
                    <AlertTriangle size={11} className="inline mr-0.5" />Requires human approval
                  </span>
                )}
                <button className="btn btn-ghost ml-auto text-[11.5px] whitespace-nowrap" onClick={draftReply}
                        disabled={busy} data-testid="draft-reply">
                  <Sparkles size={12} className={drafting ? "animate-spin" : ""} /> {drafting ? "Drafting…" : "Draft with AI"}
                </button>
              </div>
              <textarea
                data-testid="reply-textarea"
                value={reply} onChange={(e) => setReply(e.target.value)}
                rows={7}
                className="input"
                placeholder="AI-drafted reply will appear here…"
              />
              <div className="mt-2 flex flex-wrap sm:flex-nowrap items-center gap-2">
                <button className="btn btn-danger whitespace-nowrap shrink-0" data-testid="approve-send"
                        disabled={busy || !reply.trim()} onClick={sendReply}>
                  <Shield size={13} /> Propose reply
                </button>
                <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  Proposing a reply creates an action that a human must approve before anything is sent.
                </span>
              </div>
            </div>
            </>)}

            {/* Notes */}
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Internal notes</div>
              <div className="space-y-2 mb-2">
                {(c.notes || []).map((n) => (
                  <div key={n.id} className="card p-3 text-[12.5px]" data-testid={`note-${n.id}`}>
                    <div className="text-[10.5px] mb-1 font-mono" style={{ color: "var(--text-muted)" }}>{n.by} · {fmtTimeAgo(n.at)}</div>
                    {n.text}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="note-input" />
                <button className="btn btn-ghost" disabled={busy || !note.trim()} onClick={addNote} data-testid="add-note">Add</button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Intelligence + Decision + Customer 360 mini */}
        <div className="col-span-12 lg:col-span-4 order-2 lg:order-none border-b lg:border-b-0 lg:overflow-y-auto" style={{ borderColor: "var(--border)" }} data-testid="case-ai-panel">
          <div className="px-5 py-5 space-y-4">
            {links.length > 0 && (
              <div className="card p-4" data-testid="case-links">
                <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                  Linked tickets
                </div>
                {links.map((l) => (
                  <React.Fragment key={l.id}>
                  <div className="flex items-center justify-between py-1.5 text-[12.5px]"
                       data-testid={`case-link-${l.external_id}`}>
                    <span className="font-mono">
                      {l.external_url
                        ? <a href={l.external_url} target="_blank" rel="noreferrer"
                             className="underline">{l.external_id}</a>
                        : l.external_id}
                      {l.external_status && (
                        <span className="ml-2" style={{ color: "var(--text-muted)" }}>
                          {l.external_status}
                        </span>
                      )}
                    </span>
                    {/* A push is asynchronous and only confirmed by observing the
                        ticket later, so "syncing" is the honest word — a tick
                        would claim something we do not yet know. */}
                    {l.pending ? (
                      <span className="badge b-wait">syncing → {l.pending}</span>
                    ) : !l.sync_enabled ? (
                      // Repeated failure stopped Prioris writing into this one
                      // ticket. There has to be a way back, or a transient
                      // outage orphans every case in flight during it.
                      <button className="badge b-rev" data-testid={`resync-${l.external_id}`}
                              title={l.last_push_error || "Writing to this ticket was stopped"}
                              onClick={() => resync(l)} disabled={busy}>
                        sync off — retry
                      </button>
                    ) : l.last_push_error ? (
                      // The last status was not written, and the reason is the
                      // one thing that says what to fix. Before this a refused
                      // move read as "in step".
                      <span className="badge b-wait">not updated</span>
                    ) : (
                      <span className="badge b-healthy">in step</span>
                    )}
                  </div>
                  {l.sync_enabled && !l.pending && l.last_push_error && (
                    <p className="text-[11px] pb-1.5" style={{ color: "var(--text-muted)" }}
                       data-testid={`push-error-${l.external_id}`}>
                      {l.last_push_error}
                    </p>
                  )}
                  </React.Fragment>
                ))}
              </div>
            )}
            {/* Decision — what to do, first. This and Actions below are the
                only things on this page that require a human's judgement;
                everything else is supporting context. */}
            {dec && (
              <div className="card p-4" data-testid="case-decision">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Shield size={13} style={{ color: "var(--risk-product)" }} />
                  <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Decision (Decision Engine)</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-[13.5px]">{dec.title}</div>
                  {dec.revision && (
                    <span className="badge b-muted font-mono" data-testid="decision-revision"
                          title="Each Recommend actions run is a new version; earlier ones are kept as history.">
                      v{dec.revision}{dec.generated_at ? ` · ${fmtTimeAgo(dec.generated_at)}` : ""}
                    </span>
                  )}
                </div>
                {dec.recommended_actions?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10.5px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>Recommended business actions</div>
                    <div className="space-y-2">
                      {dec.recommended_actions.map((a) => (
                        <div key={a.id} className="px-2.5 py-2 rounded-md"
                             style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                             data-testid={`recommendation-${a.kind}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-[12px] font-medium">{a.title}</div>
                            <span className="badge b-muted">{Math.round((a.confidence || 0) * 100)}%</span>
                          </div>
                          <ul className="mt-1.5 space-y-1" data-testid={`recommendation-points-${a.kind}`}
                              title={a.rationale}>
                            {toPoints(a.key_points, a.rationale, 3).map((pt, i) => (
                              <li key={i} className="flex gap-1.5 text-[11.5px] leading-snug" style={{ color: "var(--text-secondary)" }}>
                                <span className="mt-[6px] w-1 h-1 rounded-full shrink-0" style={{ background: "var(--text-muted)" }} />
                                <span>{pt}</span>
                              </li>
                            ))}
                          </ul>
                          {/* Once proposed, show where its action is rather
                              than offering to queue the same thing again. */}
                          {a.action && !(a.proposable ?? false) ? (
                            <span className={`badge ${actionBadge(a.action.status)} mt-2 inline-block`}
                                  data-testid={`recommendation-action-${a.kind}`}>
                              action {a.action.status.replace(/_/g, " ")}
                            </span>
                          ) : (a.proposable ?? a.status === "open") ? (
                            <button className="btn btn-ghost mt-2" style={{ padding: "4px 10px", fontSize: 11 }}
                                    onClick={() => proposeAction(a)} disabled={busy}
                                    data-testid={`propose-${a.kind}`}>
                              {proposingId === a.id
                                ? (<><Loader2 size={11} className="animate-spin" /> Proposing…</>)
                                : a.action ? "Propose again" : "Propose this action"}
                            </button>
                          ) : (
                            <span className="badge b-healthy mt-2 inline-block">{a.status}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {dec.history?.length > 0 && (
                  <div className="mt-3 pt-2.5" style={{ borderTop: "1px solid var(--border)" }}>
                    <button type="button" className="text-[10.5px] uppercase tracking-wider flex items-center gap-1"
                            style={{ color: "var(--text-muted)" }}
                            onClick={() => setShowRecHistory((v) => !v)} data-testid="toggle-recommendation-history">
                      Earlier recommendations ({dec.history.length} version{dec.history.length === 1 ? "" : "s"})
                      <ChevronDown size={11} style={{ transform: showRecHistory ? "rotate(180deg)" : "none" }} />
                    </button>
                    {showRecHistory && (
                      <div className="mt-2 space-y-2.5" data-testid="recommendation-history">
                        {dec.history.map((h) => (
                          <div key={h.revision}>
                            <div className="font-mono text-[10.5px]" style={{ color: "var(--text-muted)" }}>
                              v{h.revision}{h.generated_at ? ` · ${fmtTimeAgo(h.generated_at)}` : ""}
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {h.recommended_actions.map((r) => (
                                <li key={r.id} className="flex items-center justify-between gap-2 text-[11.5px]">
                                  <span style={{ color: "var(--text-secondary)" }}>{r.title}</span>
                                  <span className={`badge ${r.action ? actionBadge(r.action.status) : "b-muted"}`} style={{ fontSize: 10 }}>
                                    {r.action ? `action ${r.action.status.replace(/_/g, " ")}` : r.status}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions — the approval gate, visible */}
            {(c.actions || []).length > 0 && (
              <div className="card p-4" data-testid="actions-panel">
                <div className="flex items-center gap-1.5 mb-2.5">
                  <Shield size={13} style={{ color: "var(--risk-product)" }} />
                  <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    Actions
                  </div>
                </div>
                {openActions.length === 0 && (
                  <div className="text-[11.5px] mb-2" style={{ color: "var(--text-muted)" }} data-testid="actions-none-open">
                    Nothing waiting on a decision.
                  </div>
                )}
                <div className="space-y-2">
                  {openActions.map((a) => (
                    <div key={a.id} className="px-2.5 py-2 rounded-md"
                         style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                         data-testid={`action-${a.kind}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[12px] font-medium">{a.title}</div>
                        <span className={`badge ${actionBadge(a.status)}`}>
                          {a.status.replace(/_/g, " ")}
                        </span>
                      </div>

                      {/* Why the policy engine put it in this state. */}
                      {a.policy?.explanation && (
                        <div className="text-[11px] mt-1 leading-snug" style={{ color: "var(--text-muted)" }}>
                          {a.policy.explanation}
                        </div>
                      )}
                      {a.simulated && (
                        <div className="text-[10.5px] mt-1" style={{ color: "var(--risk-sla)" }}>
                          Simulated — nothing left the system.
                        </div>
                      )}

                      <div className="flex items-center gap-1.5 mt-2">
                        {a.status === "awaiting_approval" && (
                          <>
                            <button className="btn btn-danger" style={{ padding: "4px 10px", fontSize: 11 }}
                                    onClick={() => approveAction(a.id)} disabled={busy}
                                    data-testid={`approve-${a.kind}`}>
                              <Check size={11} /> Approve
                            </button>
                            <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}
                                    onClick={() => rejectAction(a.id)} disabled={busy}
                                    data-testid={`reject-${a.kind}`}>
                              Reject
                            </button>
                          </>
                        )}
                        {a.status === "approved" && (
                          <button className="btn" style={{ padding: "4px 10px", fontSize: 11 }}
                                  onClick={() => executeAction(a.id)} disabled={busy}
                                  data-testid={`execute-${a.kind}`}>
                            {executingId === a.id
                              ? (<><Loader2 size={11} className="animate-spin" /> Executing…</>)
                              : "Execute"}
                          </button>
                        )}
                        {a.status === "executed" && a.result?.detail && (
                          <span className="text-[10.5px]" style={{ color: "var(--text-muted)" }}>
                            {a.result.detail}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Finished actions are a log, not a to-do list: one line each,
                    newest first, so repeats read as history rather than work. */}
                {doneActions.length > 0 && (
                  <div className={openActions.length ? "mt-3 pt-2.5" : ""}
                       style={openActions.length ? { borderTop: "1px solid var(--border)" } : {}}
                       data-testid="actions-history">
                    <div className="text-[10.5px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>
                      History ({doneActions.length})
                    </div>
                    <ul className="space-y-1.5">
                      {(showAllDone ? doneActions : doneActions.slice(0, 3)).map((a) => (
                        <li key={a.id} className="text-[11.5px]" data-testid={`action-${a.kind}`}>
                          <div className="flex items-center gap-2">
                            <span className="truncate">{a.title}</span>
                            <span className={`badge ${actionBadge(a.status)} shrink-0`} style={{ fontSize: 10 }}>
                              {a.status.replace(/_/g, " ")}
                            </span>
                            <span className="font-mono text-[10px] ml-auto shrink-0" style={{ color: "var(--text-muted)" }}>
                              {fmtTimeAgo(a.executed_at || a.created_at)}
                            </span>
                          </div>
                          {(a.result?.detail || a.rejection_reason || a.error) && (
                            <div className="text-[10.5px] leading-snug mt-0.5" style={{ color: "var(--text-muted)" }}>
                              {a.result?.detail || a.rejection_reason || a.error}
                            </div>
                          )}
                          {a.simulated && (
                            <div className="text-[10.5px]" style={{ color: "var(--risk-sla)" }}>
                              Simulated — nothing left the system.
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    {doneActions.length > 3 && (
                      <button type="button" className="text-[10.5px] mt-1.5 underline-offset-2 hover:underline"
                              style={{ color: "var(--text-secondary)" }}
                              onClick={() => setShowAllDone((v) => !v)} data-testid="toggle-actions-history">
                        {showAllDone ? "Show fewer" : `Show all ${doneActions.length}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Customer 360 mini */}
            {cust && (
              <div className="card p-4" data-testid="case-customer-mini">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Customer 360</div>
                  <Link className="text-[11px] underline-offset-2 hover:underline" to={`/customers/${cust.id}`} data-testid="open-customer">Open profile →</Link>
                </div>
                <div className="font-medium">{cust.name}</div>
                <div className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>{cust.email || cust.phone}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11.5px]">
                  <Kv k="Plan" v={cust.plan} />
                  <Kv k="Entitlement" v={cust.entitlement_active ? "active" : "inactive"} bad={!cust.entitlement_active} />
                  <Kv k="Billing" v={cust.billing_platform || "—"} />
                  <Kv k="State" v={cust.subscription_state || "—"} />
                </div>
                {cust.transaction_id && (
                  <div className="mt-2 text-[10.5px] font-mono" style={{ color: "var(--text-muted)" }}>txn: {cust.transaction_id}</div>
                )}
                <div className="mt-2 text-[11.5px]" style={{ color: "var(--text-secondary)" }}>
                  Risk score: <span className="font-mono">{customer.risk_score}</span>
                </div>
              </div>
            )}

            {/* AI analysis — the "why" behind the score and the decision
                above. Collapsed by default: it's reference material, not
                something to act on, and stacked open it drowned out the
                Decision/Actions cards a case is actually opened for. */}
            <div className="pt-1" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setShowAnalysis((v) => !v)}
                      className="w-full flex items-center justify-between py-2 hover-wash rounded-md px-1 -mx-1"
                      data-testid="toggle-ai-analysis">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                  <Sparkles size={12} /> AI analysis
                </span>
                <span className="flex items-center gap-2">
                  {!showAnalysis && c.score && (
                    <>
                      <span className={`badge ${RISK_CLASS[c.risk_type] || "b-muted"}`}>
                        {c.score.risk_band}
                      </span>
                      <span className="font-mono text-[11px]" style={{ color: "var(--text-secondary)" }}>
                        {c.score.total}/100
                      </span>
                    </>
                  )}
                  <ChevronDown size={14} style={{ color: "var(--text-muted)", transform: showAnalysis ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                </span>
              </button>

              {/* Collapsed, the panel answers one question — what is driving
                  this case — as a few pointers rather than a paragraph. */}
              {!showAnalysis && (c.score || intel) && (
                <div className="pb-2 px-1 space-y-1.5" data-testid="ai-analysis-preview">
                  {c.score && <DriverList components={topDrivers(c.score.components, 3)} compact />}
                  {intel && <RiskFlags intel={intel} />}
                </div>
              )}

              {showAnalysis && (
                <div className="space-y-4 pt-2">
                  {/* Attention score — the explainability 05 calls essential.
                      Unlike the AI panel below, this has real data today. */}
                  {c.score && (
                    <div className="card p-4" data-testid="score-breakdown">
                      <div className="flex items-center justify-between mb-2.5">
                        <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                          Attention score
                        </div>
                        <span className="font-mono text-[10px]" style={{ color: "var(--text-muted)" }}>
                          {c.score.calculation_version}
                        </span>
                      </div>

                      <div className="flex items-baseline gap-2">
                        <div className="font-display text-[34px] leading-none">{c.score.total}</div>
                        <span className="text-[12px]" style={{ color: "var(--text-muted)" }}>/ 100</span>
                        <span className={`badge ${RISK_CLASS[c.risk_type] || "b-muted"} ml-auto`}>
                          {c.score.risk_band}
                        </span>
                      </div>

                      {/* What moved the score, biggest first. Factors that
                          added nothing are one line, not six empty bars; the
                          full reasoning and raw evidence are one click away. */}
                      <div className="mt-3">
                        <div className="text-[10.5px] uppercase tracking-wider mb-1.5" style={{ color: "var(--text-muted)" }}>
                          What's driving it
                        </div>
                        <DriverList components={topDrivers(c.score.components)} detailed={showCalc} />
                        {c.score.components.some((comp) => !comp.score) && (
                          <div className="text-[10.5px] mt-2" style={{ color: "var(--text-muted)" }} data-testid="score-not-factors">
                            Not a factor: {c.score.components.filter((comp) => !comp.score).map((comp) => comp.label).join(" · ")}
                          </div>
                        )}
                        <button type="button" className="text-[10.5px] mt-2 underline-offset-2 hover:underline"
                                style={{ color: "var(--text-secondary)" }}
                                onClick={() => setShowCalc((v) => !v)} data-testid="toggle-score-calc">
                          {showCalc ? "Hide calculation" : "Show calculation"}
                        </button>
                      </div>

                      {c.score.missing_evidence?.length > 0 && (
                        <div className="mt-3 pt-2.5" style={{ borderTop: "1px solid var(--border)" }}>
                          <div className="text-[10.5px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                            Couldn't assess
                          </div>
                          <ul className="text-[11px] space-y-0.5" style={{ color: "var(--text-secondary)" }}>
                            {c.score.missing_evidence.map((m, i) => <li key={i}>— {m}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Intelligence */}
                  <div className="card p-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Sparkles size={13} style={{ color: "var(--risk-waiting)" }} />
                      <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>AI Intelligence (informs only)</div>
                    </div>
                    {intel ? (
                      <>
                        {/* Points first: nobody reads a paragraph mid-escalation. */}
                        <ul className="space-y-1.5" data-testid="ai-key-points">
                          {keyPoints(intel).map((p, i) => (
                            <li key={i} className="flex gap-2 text-[12.5px] leading-snug">
                              <span className="mt-[6px] w-1 h-1 rounded-full shrink-0" style={{ background: "var(--risk-waiting)" }} />
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                        {intel.key_points?.length > 0 && intel.summary && (
                          <div className="mt-2">
                            <button type="button" className="text-[10.5px] underline-offset-2 hover:underline"
                                    style={{ color: "var(--text-secondary)" }}
                                    onClick={() => setShowSummary((v) => !v)} data-testid="toggle-ai-summary">
                              {showSummary ? "Hide full summary" : "Read full summary"}
                            </button>
                            {showSummary && (
                              <p className="text-[12px] leading-snug mt-1" style={{ color: "var(--text-secondary)" }}
                                 data-testid="ai-summary">{intel.summary}</p>
                            )}
                          </div>
                        )}
                        {/* Sentiment is not assessed yet ("n/a"), so it gets no tile. */}
                        <div className={`mt-3 grid ${hasSentiment(intel) ? "grid-cols-3" : "grid-cols-2"} gap-2 text-center`}>
                          <Stat label="Urgency" value={intel.urgency_score} suffix="" />
                          <Stat label="Confidence" value={Math.round(intel.confidence_score * 100)} suffix="%" />
                          {hasSentiment(intel) && <Stat label="Sentiment" string={intel.sentiment} />}
                        </div>
                        <div className="mt-3">
                          <RiskFlags intel={intel} withCategory />
                        </div>
                        {intel.missing_information?.length > 0 && (
                          <div className="mt-3">
                            <div className="text-[10.5px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>To confirm</div>
                            <ul className="text-[11.5px] space-y-0.5" style={{ color: "var(--text-secondary)" }}>
                              {intel.missing_information.map((m, i) => <li key={i}>— {m}</li>)}
                            </ul>
                          </div>
                        )}
                        {intel.evidence?.length > 0 && (
                          <div className="mt-3">
                            <button type="button" className="text-[10.5px] uppercase tracking-wider flex items-center gap-1"
                                    style={{ color: "var(--text-muted)" }}
                                    onClick={() => setShowEvidence((v) => !v)} data-testid="toggle-ai-evidence">
                              Evidence ({intel.evidence.length})
                              <ChevronDown size={11} style={{ transform: showEvidence ? "rotate(180deg)" : "none" }} />
                            </button>
                            {showEvidence && (
                              <div className="text-[11.5px] space-y-1.5 mt-1.5" data-testid="ai-evidence">
                                {intel.evidence.map((e, i) => {
                                  const { claim, quote, inference } = splitEvidence(e);
                                  return (
                                    <div key={i} className="leading-snug">
                                      <div>› {claim}{inference && <span className="badge b-muted ml-1.5">inference</span>}</div>
                                      {quote && <div className="pl-3 text-[11px] italic" style={{ color: "var(--text-muted)" }}>“{quote}”</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                        Intelligence not yet generated. Click <span className="font-medium" style={{ color: "var(--text)" }}>Re-run AI</span> above.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const DONE_ACTION = new Set(["executed", "rejected", "failed"]);

const actionBadge = (status) =>
  status === "executed" ? "b-healthy"
  : status === "rejected" || status === "failed" ? "b-rev"
  : status === "awaiting_approval" ? "b-sla" : "b-muted";

// Score components that added something, largest share of their maximum first.
const topDrivers = (components = [], limit) => {
  const ranked = components
    .filter((comp) => comp.score > 0)
    .sort((a, b) => b.score / (b.max_score || 1) - a.score / (a.max_score || 1) || b.score - a.score);
  return limit ? ranked.slice(0, limit) : ranked;
};

const evidenceValues = (comp, key) =>
  (comp.evidence || []).filter((e) => e.startsWith(`${key}=`)).map((e) => e.slice(key.length + 1));

// A few words for a component, read from its evidence where that is shorter
// than the sentence the scorer wrote.
const driverPointer = (comp) => {
  const plan = evidenceValues(comp, "plan")[0];
  const severity = evidenceValues(comp, "severity")[0];
  const phrases = evidenceValues(comp, "phrase");
  if (comp.key === "customer_value" && plan) return `${plan} plan`;
  if (comp.key === "severity" && severity) return `${severity} severity`;
  if (phrases.length) {
    const shown = phrases.slice(0, 2).map((p) => `“${p}”`).join(", ");
    return phrases.length > 2 ? `${shown} +${phrases.length - 2} more` : shown;
  }
  const reason = (comp.reason || "").replace(/\.$/, "");
  // "SLA / age — SLA breached 12h ago" says SLA twice.
  return comp.key === "sla_age" ? reason.replace(/^SLA /, "") : reason;
};

const barColor = (pct) =>
  pct > 0.66 ? "var(--risk-rev)" : pct > 0.33 ? "var(--risk-sla)" : "var(--risk-healthy)";

const DriverList = ({ components, compact = false, detailed = false }) => {
  if (!components.length) {
    return <div className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>No risk factors raised the score.</div>;
  }
  return (
    <ul className={compact ? "space-y-1" : "space-y-2"} data-testid="score-drivers">
      {components.map((comp) => {
        const pct = comp.max_score ? comp.score / comp.max_score : 0;
        return (
          <li key={comp.key} className="text-[11.5px]">
            {/* Label and score on one line, the pointer under it: the column
                is narrow, and a truncated pointer would hide the point. */}
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: barColor(pct) }} />
              <span className="font-medium">{comp.label}</span>
              <span className="font-mono text-[10.5px] ml-auto shrink-0" style={{ color: "var(--text-muted)" }}>
                {comp.score}/{comp.max_score}
              </span>
            </div>
            <div className="ml-3.5 text-[11px] leading-snug" style={{ color: "var(--text-secondary)" }} title={comp.reason}>
              {driverPointer(comp)}
            </div>
            {!compact && (
              <div className="h-1 rounded-full mt-1 ml-3.5" style={{ background: "var(--surface-2)" }}>
                <div className="h-1 rounded-full" style={{ width: `${pct * 100}%`, background: barColor(pct) }} />
              </div>
            )}
            {detailed && (
              <div className="ml-3.5 mt-1">
                <div className="text-[10.5px] leading-snug" style={{ color: "var(--text-muted)" }}>{comp.reason}</div>
                {comp.evidence?.length > 0 && (
                  <div className="font-mono text-[10px] mt-0.5 break-all" style={{ color: "var(--text-secondary)" }}>
                    {comp.evidence.join(" · ")}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const RISK_FLAGS = [
  ["revenue_risk", "revenue risk"],
  ["trust_risk", "trust risk"],
  ["churn_risk", "churn risk"],
  ["sla_risk", "SLA risk"],
];

const RiskFlags = ({ intel, withCategory = false }) => {
  const flags = RISK_FLAGS.filter(([key]) => intel[key]);
  const category = withCategory && intel.category ? (CATEGORY_LABEL[intel.category] || intel.category) : null;
  if (!flags.length && !category) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="ai-risk-flags">
      {flags.map(([key, label]) => <span key={key} className="badge b-rev">{label}</span>)}
      {category && <span className="badge b-muted">{category}</span>}
    </div>
  );
};

const hasSentiment = (intel) => intel.sentiment && intel.sentiment !== "n/a";

// The API renders evidence as `claim — "quote" [inference]`; split it back so
// the claim reads first and the supporting quote sits quietly beneath it.
// The model's own points when it gave them; for anything stored before it
// did, the prose broken into one point per sentence.
const toPoints = (points, text, max) => {
  if (points?.length) return points.slice(0, max);
  return (text || "")
    .split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/)
    .map((s) => s.trim().replace(/\.$/, ""))
    .filter(Boolean)
    .slice(0, max);
};

const keyPoints = (intel) => toPoints(intel.key_points, intel.summary, 4);

const splitEvidence = (text) => {
  const inference = text.endsWith(" [inference]");
  const body = inference ? text.slice(0, -" [inference]".length) : text;
  const match = body.match(/^(.*?) — "(.*)"$/s);
  return match ? { claim: match[1], quote: match[2], inference } : { claim: body, quote: null, inference };
};

const Stat = ({ label, value, string, suffix = "" }) => (
  <div className="rounded-md py-2" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
    <div className="font-mono text-[16px]">{string ?? value}{suffix}</div>
    <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: "var(--text-muted)" }}>{label}</div>
  </div>
);

const Kv = ({ k, v, bad }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{k}</div>
    <div className={bad ? "text-rev" : ""}>{v}</div>
  </div>
);
